import React, { useEffect, useState } from "react";
import { Modal, Radio } from "antd";
import { StageId } from "ppt/constants";
import styles from "./pptStatusLifecycle.module.scss";

/**
 * Modal for the "Send back" action — reverts the promotion to an earlier
 * stage. Captures:
 *   - target stage  : radio of every stage before the current one (or all stages if lifecycle is complete)
 *   - reason (free) : required textarea (audit log payload)
 *
 * The Send back button stays disabled until both a target is selected and
 * the reason is non-blank. Default selection is the immediately previous
 * stage (most common send-back), per the design briefs.
 *
 * DATA_SYNC stage is excluded from send-back options as it transitions
 * automatically and cannot be manually re-entered.
 */
export const SendBackModal = ({
  open,
  stages, // full ordered list, used to render radio options
  currentStageIndex,
  onCancel,
  onConfirm,
  isConfirming,
}) => {
  // Show only stages before the current one.
  // When currentStageIndex is -1 (all stages complete), show all stages.
  // Filter out DATA_SYNC stage as it cannot be returned to manually.
  const previousStages =
    currentStageIndex < 0
      ? stages.filter((s) => s.id !== StageId.DATA_SYNC) // when done, can send back to any stage except data_sync
      : stages.slice(0, Math.max(currentStageIndex, 0)).filter((s) => s.id !== StageId.DATA_SYNC);
  const defaultTarget =
    previousStages.length > 0
      ? previousStages[previousStages.length - 1].id
      : null;

  const [targetStageId, setTargetStageId] = useState(defaultTarget);
  const [reason, setReason] = useState("");

  // Reset on each open so previous attempts don't leak between sessions.
  useEffect(() => {
    if (open) {
      setTargetStageId(defaultTarget);
      setReason("");
    }
  }, [open, defaultTarget]);

  const canSubmit = !!targetStageId && reason.trim().length > 0;

  return (
    <Modal
      title="Send back to a previous stage"
      open={open}
      onCancel={onCancel}
      onOk={() => onConfirm({ stageId: targetStageId, reason: reason.trim() })}
      okText="Send back"
      cancelText="Cancel"
      okButtonProps={{ disabled: !canSubmit, loading: isConfirming }}
      centered
      destroyOnClose
    >
      <div className={styles.modalBody}>
        <p className={styles.modalSubtitle}>
          Reverts progress. The current stage and any stages between it and the
          target will be reset to not-started.
        </p>

        <div className={styles.sendBackSection}>
          <span className={styles.sendBackLabel}>Send back to</span>
          <Radio.Group
            value={targetStageId}
            onChange={(e) => setTargetStageId(e.target.value)}
            className={styles.sendBackOptions}
          >
            {previousStages.map((stage, idx) => (
              <Radio
                key={stage.id}
                value={stage.id}
                className={styles.sendBackOption}
              >
                {idx + 1}. {stage.name}
              </Radio>
            ))}
          </Radio.Group>
        </div>

        <div className={styles.sendBackSection}>
          <span className={styles.sendBackLabel}>
            Reason <span className={styles.sendBackRequired}>(required)</span>
          </span>
          <textarea
            className={styles.sendBackReason}
            placeholder="Why is this being sent back?"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
          />
        </div>
      </div>
    </Modal>
  );
};
